<template>
  <!-- The avatar and its optional badge share one grid cell, so the badge can
       sit over the avatar's bottom-right edge: q-avatar clips its own content
       to the circle. The wrapper is exactly the avatar's box, so a caller's
       class (tw-shrink-0, an avatar-group ring or overlap) lands here and
       renders as it did on the q-avatar. -->
  <span class="c-avatar-stack">
    <q-avatar :class="classes" :size="size" :style="cssVars">
      <img
        v-if="src && imageHasError === false"
        :src="src"
        :alt="name"
        @error="imageHasError = true"
      />

      <span v-if="showInitials" class="c-avatar__initials">
        {{ initials }}
      </span>
    </q-avatar>

    <span v-if="hasBadge" class="c-avatar-stack__badge">
      <slot name="badge" />
    </span>
  </span>
</template>

<script setup lang="ts">
import { computed, ref, useSlots } from "vue";

const props = withDefaults(
  defineProps<{
    name: string;
    src?: string | null;
    size?: string;
    fontSize?: string;
    color?: string;
  }>(),
  {
    src: null,
    size: "32px",
    fontSize: undefined,
    color: undefined,
  },
);

const classes = computed(() => {
  const result = ["c-avatar"];

  if (props.color) {
    result.push(`c-avatar--${props.color}`);
  }

  return result;
});

const cssVars = computed(() =>
  props.fontSize ? { "--c-avatar-font-size": props.fontSize } : {},
);

const slots = useSlots();
const hasBadge = computed(() => slots.badge !== undefined);

const imageHasError = ref(false);

const showInitials = computed(
  () => Boolean(props.src) === false || imageHasError.value === true,
);

const initials = computed(() => {
  const { name = "" } = props;
  const parts = name.trim().split(/\s+/);

  if (parts.length === 0) {
    return "";
  }

  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }

  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
});
</script>

<style lang="scss" scoped>
.c-avatar-stack {
  @apply tw-inline-grid tw-align-middle;

  > * {
    grid-area: 1 / 1;
  }

  &__badge {
    @apply tw-flex tw-translate-x-1 tw-translate-y-1 tw-self-end tw-justify-self-end;
  }
}

.c-avatar {
  &:not([class*="c-avatar--"]) {
    @apply tw-bg-slate-200 tw-text-gray-700;
  }

  :deep(img) {
    @apply tw-object-cover;
  }

  &__initials {
    @apply tw-select-none tw-font-semibold;

    font-size: var(--c-avatar-font-size, 0.75rem);
  }
}
</style>
